import { AfterViewInit, Directive, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, inject } from '@angular/core'
import { BadgeEditorService } from '@app/modules/admin/components/badge-editor/services/badge-editor.service'

@Directive({
  selector: '[appScaleBadge]',
  standalone: true,
  providers: [BadgeEditorService]
})
export class ScaleBadgeDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input('appScaleBadge') public html: string | null | undefined = null
  @Input() public scaleBadgePadding = 12

  private static readonly _MM_TO_PX = 96 / 25.4
  private readonly _host = inject<ElementRef<HTMLElement>>(ElementRef)
  private readonly _badgeEditorService = inject(BadgeEditorService)
  private _resizeObserver: ResizeObserver | null = null

  public ngAfterViewInit(): void {
    const container = this._host.nativeElement.parentElement
    if (container && typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => this._scale())
      this._resizeObserver.observe(container)
    }
    queueMicrotask(() => this._scale())
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['html']) queueMicrotask(() => this._scale())
  }

  /**
   * Cleanup observer.
   * @returns {void}
   */
  public ngOnDestroy(): void {
    this._resizeObserver?.disconnect()
  }

  /**
   * Scale the badge to fit its container.
   */
  private _scale(): void {
    const hostElement = this._host.nativeElement
    const containerElement = hostElement.parentElement
    if (!containerElement) return

    const badgeElement = hostElement.querySelector<HTMLElement>('#badge')
    if (!badgeElement) return

    const badgeWidthMM = parseFloat(badgeElement.getAttribute('data-width') || '0')
    const badgeHeightMM = parseFloat(badgeElement.getAttribute('data-height') || '0')
    if (!badgeWidthMM || !badgeHeightMM) return

    this._applyBadgePixelDimensions(badgeElement, badgeWidthMM, badgeHeightMM)
    this._resizePersonaNameOnly(badgeElement)
    this._scaleBadgeToFitContainer(hostElement, containerElement, badgeElement)
  }

  /**
   * Sets badge dimensions in pixels based on `data-width`/`data-height` (mm).
   * @param badgeElement {HTMLElement} - `#badge` element.
   * @param badgeWidthMM {number} - Width in mm.
   * @param badgeHeightMM {number} - Height in mm.
   */
  private _applyBadgePixelDimensions(badgeElement: HTMLElement, badgeWidthMM: number, badgeHeightMM: number): void {
    badgeElement.style.width = `${badgeWidthMM * ScaleBadgeDirective._MM_TO_PX}px`
    badgeElement.style.height = `${badgeHeightMM * ScaleBadgeDirective._MM_TO_PX}px`
  }

  /**
   * Resize only the persona-name field(s) in the rendered badge.
   * Uses legacy `BadgeEditorService.resizeTextValueToFitParent` for sizing logic,
   * while restoring all non-persona-name font-sizes to keep other fields unchanged.
   *
   * @param badgeElement {HTMLElement} - `#badge` element.
   */
  private _resizePersonaNameOnly(badgeElement: HTMLElement): void {
    const personaNameElements = badgeElement.querySelectorAll<HTMLParagraphElement>('[data-element="persona_name"]')
    personaNameElements.forEach((personaNameEl) => this._resizePersonaNameInRow(personaNameEl))
  }

  /**
   * Resize a single persona-name element inside its row, preserving other row fields.
   * @param personaNameEl {HTMLParagraphElement} - Persona name `<p>` element.
   */
  private _resizePersonaNameInRow(personaNameEl: HTMLParagraphElement): void {
    const rowElement = personaNameEl.closest<HTMLDivElement>('[id^="row-"]')
    if (!rowElement) return

    const rowIndex = this._rowIndexFromId(rowElement.id)
    if (rowIndex === null) return

    const otherFontSizes = this._snapshotNonPersonaNameFontSizes(rowElement)
    this._badgeEditorService.resizeTextValueToFitParent(rowIndex, rowElement)
    this._restoreFontSizes(otherFontSizes)
  }

  /**
   * @param rowId {string} - Row element id, expected `row-N`.
   * @returns {number | null} - Zero-based row index or null if not parsable.
   */
  private _rowIndexFromId(rowId: string): number | null {
    const match = String(rowId || '').match(/^row-(\d+)$/)
    if (!match) return null
    return Math.max(0, parseInt(match[1], 10) - 1)
  }

  /**
   * Snapshot inline `font-size` values for all non-persona-name `<p>` elements in the given row.
   * @param rowElement {HTMLElement} - Row element.
   * @returns {Map<HTMLParagraphElement, string>} - Map of element to its previous `style.fontSize`.
   */
  private _snapshotNonPersonaNameFontSizes(rowElement: HTMLElement): Map<HTMLParagraphElement, string> {
    const allParagraphs = Array.from(rowElement.querySelectorAll<HTMLParagraphElement>('p'))
    const snapshot = new Map<HTMLParagraphElement, string>()
    allParagraphs.forEach((el) => {
      if (el.getAttribute('data-element') === 'persona_name') return
      snapshot.set(el, el.style.fontSize)
    })
    return snapshot
  }

  /**
   * Restore inline `font-size` values from a snapshot map.
   * @param snapshot {Map<HTMLParagraphElement, string>} - Map created by `_snapshotNonPersonaNameFontSizes`.
   * @returns {void}
   */
  private _restoreFontSizes(snapshot: Map<HTMLParagraphElement, string>): void {
    snapshot.forEach((fontSize, el) => {
      el.style.fontSize = fontSize
    })
  }

  /**
   * Scale the rendered badge wrapper so the badge fits inside its container.
   * @param hostElement {HTMLElement} - Wrapper element that receives the CSS `scale`.
   * @param containerElement {HTMLElement} - Card container element.
   * @param badgeElement {HTMLElement} - `#badge` element.
   * @returns {void}
   */
  private _scaleBadgeToFitContainer(hostElement: HTMLElement, containerElement: HTMLElement, badgeElement: HTMLElement): void {
    const paddingPx = this.scaleBadgePadding
    const scaleWidth = (containerElement.clientWidth - paddingPx * 2) / badgeElement.offsetWidth
    const scaleHeight = (containerElement.clientHeight - paddingPx * 2) / badgeElement.offsetHeight
    const scale = Math.max(0, Math.min(scaleWidth, scaleHeight))
    hostElement.style.scale = scale.toString()
  }
}
